/* Users — user management table. Lists name, email, tier, status,
   assigned licenses, last login; per-row actions for tier change,
   approve, and license assignment. Surfaces real fetch errors. */
import React, { useState, useEffect, useRef } from "react";
import { P, FC } from "../../shared/constants";
import DataTable from "../components/DataTable";
import EmptyState from "../components/EmptyState";
import ErrorState from "../components/ErrorState";
import Skeleton from "../components/Skeleton";
import Chip from "../components/Chip";
import { Search, Refresh, Mail, Users as UsersIcon } from "../icons";
var E = React.createElement;

var TIERS = ["free","starter","pro","admin"];

export default function Users(props){
  var users    = props.users    || [];
  var loading  = !!props.loading;
  var error    = props.error    || null;
  var updating = props.updatingId || null;

  /* PR-5.6: auto-fetch on mount so the Users tab doesn't open empty
     with a "Click Refresh" CTA. Guarded by a ref so a re-render
     (e.g. parent state churn after the first fetch) doesn't re-fire
     and waste a request. Only fires the parent's onRefresh once;
     subsequent refreshes still go through the toolbar button. */
  var firedRef = useRef(false);
  useEffect(function(){
    if (firedRef.current) return;
    if (loading || error) return;
    if (users.length > 0) { firedRef.current = true; return; }
    if (typeof props.onRefresh === "function") {
      firedRef.current = true;
      props.onRefresh();
    }
  }, [users.length, loading, error, props.onRefresh]);

  var st = useState({ q:"", tier:"" });
  var f = st[0], setF = st[1];

  var rows = users.filter(function(u){
    if(f.tier && u.tier !== f.tier) return false;
    if(f.q){
      var hay = ((u.name||"")+" "+(u.email||"")+" "+(u.id||"")).toLowerCase();
      if(hay.indexOf(f.q.toLowerCase()) < 0) return false;
    }
    return true;
  });

  var columns = [
    { key:"who", label:"Name / Email", render:function(u){
        return E("div",null,
          E("div",{style:{fontWeight:700}}, u.name || "(no name)"),
          E("div",{style:{fontSize:11,color:P.slateM,fontFamily:FC}}, u.email||u.id));
      } },
    { key:"tier", label:"Tier", render:function(u){
        var busy = updating===u.id;
        return E("select", {
          value:u.tier||"free", disabled:busy,
          "aria-label":"Change tier for "+(u.email||u.id),
          onChange:function(e){ props.onChangeTier && props.onChangeTier(u, e.target.value); },
          style:{padding:"5px 8px",borderRadius:6,border:"1px solid "+P.bdr,fontSize:11,fontWeight:700,
            color:P.slate,background:P.card,minHeight:32}
        }, TIERS.map(function(t){ return E("option",{key:t,value:t}, t); }));
      } },
    { key:"status", label:"Status", render:function(u){
        return E(Chip,{
          variant:u.approved?"emerald":"amber", size:"sm"
        }, u.approved?"Active":"Pending");
      } },
    { key:"licenses", label:"Licenses", render:function(u){
        if(u.tier==="admin") return E(Chip,{variant:"coral",size:"sm"}, "All (admin)");
        var list = u.licenses || [];
        if(list.length===0) return E("span",{style:{fontSize:11,color:P.slateM,fontStyle:"italic"}},"none");
        var title = list.map(function(l){return l.domain}).join(", ");
        return E("span",{title:title,style:{display:"inline-flex",alignItems:"center",gap:6}},
          E("span",{style:{fontWeight:700}}, list.length),
          E("span",{style:{fontSize:11,color:P.slateM}}, list.length===1?"license":"licenses"));
      } },
    { key:"lastLogin", label:"Last login", render:function(u){
        return E("span",{style:{fontSize:11,color:P.slateM,fontFamily:FC}},
          u.lastLogin?u.lastLogin.split("T")[0]:"—");
      } },
    { key:"actions", label:"", align:"right", render:function(u){
        var busy = updating===u.id;
        return E("div",{style:{display:"inline-flex",gap:4,flexWrap:"wrap",justifyContent:"flex-end"}},
          E("button",{type:"button",disabled:busy,
            "aria-label":"Assign licenses to "+(u.email||u.id),
            onClick:function(e){ e.stopPropagation(); props.onAssignLicenses && props.onAssignLicenses(u); },
            style:{padding:"6px 10px",borderRadius:6,border:"1px solid "+P.pri,
              background:P.priLt,color:P.pri,fontSize:11,fontWeight:700,cursor:"pointer",minHeight:32}},
            "Licenses"),
          E("button",{type:"button",disabled:busy,
            "aria-label":(u.approved?"Suspend ":"Approve ")+(u.email||u.id),
            onClick:function(e){ e.stopPropagation(); props.onToggleApproved && props.onToggleApproved(u); },
            style:{padding:"6px 10px",borderRadius:6,border:"none",
              background:u.approved?P.coralLt:P.emeraldLt,
              color:u.approved?(P.coralDk||P.coral):(P.emeraldDk||P.emerald),
              fontSize:11,fontWeight:700,cursor:"pointer",minHeight:32}},
            u.approved?"Suspend":"Approve"));
      } }
  ];

  /* Filter bar + actions */
  var filterBar = E("div",{role:"search","aria-label":"Filter users",
    style:{display:"flex",flexWrap:"wrap",gap:8,padding:"12px 14px",
      borderRadius:12,background:P.bgS,border:"1px solid "+P.bdr,marginBottom:14}},
    E("label",{style:{display:"flex",alignItems:"center",gap:6,
      background:P.card,padding:"0 10px",borderRadius:8,border:"1px solid "+P.bdr,flex:"1 1 240px",minHeight:36}},
      E(Search,{size:14}),
      E("span",{style:{position:"absolute",width:1,height:1,padding:0,margin:-1,overflow:"hidden",clip:"rect(0,0,0,0)",border:0}},"Search users"),
      E("input",{type:"text",value:f.q,
        onChange:function(e){setF(Object.assign({},f,{q:e.target.value}))},
        placeholder:"Search name, email, ID…",
        style:{border:"none",outline:"none",fontSize:12,flex:1,padding:"8px 0",
          background:"transparent",minHeight:36,fontFamily:"inherit",minWidth:0}})),
    E("label",{style:{display:"flex",alignItems:"center",gap:6}},
      E("span",{style:{fontSize:11,fontWeight:700,color:P.slateM}},"Tier:"),
      E("select",{value:f.tier,
        onChange:function(e){setF(Object.assign({},f,{tier:e.target.value}))},
        style:{padding:"6px 10px",borderRadius:8,border:"1px solid "+P.bdr,fontSize:12,minHeight:36,background:P.card}},
        E("option",{value:""},"All"),
        TIERS.map(function(t){ return E("option",{key:t,value:t}, t.charAt(0).toUpperCase()+t.slice(1)); }))),
    E("div",{style:{flex:"1 0 auto"}}),
    /* PR-5.6: Refresh + Invite share a sub-container with
       flex-wrap:nowrap so they stay glued together at the right edge
       of the toolbar even when the search/tier controls wrap onto
       their own line on narrow viewports. */
    E("div",{style:{display:"flex",gap:8,flexWrap:"nowrap",alignItems:"center",marginLeft:"auto"}},
      E("button",{type:"button",onClick:props.onRefresh,
        style:{padding:"8px 12px",borderRadius:8,border:"1px solid "+P.bdr,
          background:P.card,fontSize:11,fontWeight:600,cursor:"pointer",minHeight:36,
          display:"inline-flex",alignItems:"center",gap:6}},
        E(Refresh,{size:13}),"Refresh"),
      E("button",{type:"button",onClick:props.onInvite,
        style:{padding:"8px 12px",borderRadius:8,border:"none",
          background:P.pri,color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer",minHeight:36,
          display:"inline-flex",alignItems:"center",gap:6}},
        E(Mail,{size:13}),"Invite user"))
  );

  var body;
  if(loading)    body = E(Skeleton,{rows:6,height:24,label:"Loading users…"});
  else if(error) body = E(ErrorState,{title:"We couldn't load users.",
                          message:error, onRetry:props.onRefresh});
  else if(users.length===0)
                 body = E(EmptyState,{icon:E(UsersIcon,{size:32}),
                          title:"No users loaded.",
                          message:"Click Refresh to fetch users from the directory.",
                          action:E("button",{type:"button",onClick:props.onRefresh,
                            style:{padding:"8px 14px",borderRadius:8,border:"1px solid "+P.bdr,
                              background:P.card,fontSize:11,fontWeight:700,cursor:"pointer",minHeight:36}},
                            "Refresh")});
  else if(rows.length===0)
                 body = E(EmptyState,{title:"No users match the current filters."});
  else           body = E(DataTable,{columns:columns,rows:rows,
                          rowKey:function(u){return u.id}});

  return E("div",null, filterBar, body);
}
